import React, {useEffect, useState} from 'react';
import '../../../../assets/css/modal.css';
import {useCssTemplateStore} from '../../../../store/useCssTemplateStore';
import {
    BasicCreate,
    CssImage,
    CssImageCreate,
    CssImageCreateRequest,
    CssImageRequest,
    CssTemplateResponse,
    ImageCreate,
    ThemeCreate
} from "../../../../type/cssTemplate";
import {toast} from "react-toastify";
import apiClient from "../../../../service/apiClient";
import {useLoginStore} from "../../../../store/useLogin";
import {getAspectRatioType, isDefaultTheme, makeDeleteKey, normalizeName, parseDeleteKey} from "../../../../util/templateImageUtils";
import TemplateSidebar from "./templateImage/TemplateSidebar";
import TemplateThemeList from "./templateImage/TemplateThemeList";
import {ThemeInfo} from "./templateImage/types";

interface ITemplateImageModal {
    isOpen: boolean;
    onClose: () => void;
    selectedCategory?: string;
    title?: string;
    templateId?: string;
    productId?: string;
}

/**
 * @author pjh
 * @date 2025.08.19
 * @description 템플릿 CSS 이미지 선택 모달
 */
const TemplateImageModal: React.FC<ITemplateImageModal> = ({
    isOpen,
    onClose,
    selectedCategory = 'all',
    title = '템플릿 이미지 선택',
    templateId = 'default',
    productId = ''
}) => {
    const { role } = useLoginStore();
    const [selectedThemeName, setSelectedThemeName] = useState<string | null>(null); // 선택된 테마
    const [showSidebar, setShowSidebar] = useState(false); // 사이드바 표시 여부
    const [selectedMainTheme, setSelectedMainTheme] = useState<string | null>(null); // isMain이 true인 테마 (라디오 선택)
    const [template, setTemplate] = useState<CssTemplateResponse>();
    const [loading, setLoading] = useState(false);
    const [files, setFiles] = useState<File[]>([]);
    const [saveList, setSaveList] = useState<CssImageRequest[]>([]);
    const [createList, setCreateList] = useState<CssImageCreate[]>([]);
    const [uploadedImages, setUploadedImages] = useState<Map<string, string>>(new Map()); // themeName_cssName -> 새 이미지 URL
    const [uploadedImagesInfo, setUploadedImagesInfo] = useState<Map<string, any>>(new Map()); // 업로드된 이미지 정보
    const [isUploading, setIsUploading] = useState(false); // 업로드 중 상태
    const [deleteCssNames, setDeleteCssNames] = useState<Set<string>>(new Set()); // 삭제(복구)할 themeName_cssName들
    const [showNewThemeInput, setShowNewThemeInput] = useState(false); // 새 테마 입력 표시
    const [newThemeName, setNewThemeName] = useState(''); // 새 테마 이름
    const [newThemes, setNewThemes] = useState<ThemeCreate[]>([]); // 새로 추가된 테마들
    const [selectedNewTheme, setSelectedNewTheme] = useState<string | null>(null); // 선택된 새 테마
    const [showNewCategoryInput, setShowNewCategoryInput] = useState(false); // 새 카테고리 입력 표시
    const [newCategoryName, setNewCategoryName] = useState(''); // 새 카테고리 이름
    const [availableThemes, setAvailableThemes] = useState<ThemeInfo[]>([]);

    const { getCssTemplate, saveBasicCssTemplate, saveNewThemes, updateThemesForUser } = useCssTemplateStore();

    // 기본 테마 판별 유틸
    const isDefaultThemeByName = (themeName?: string | null) => {
        if (!themeName) return false;
        const found = template?.categories?.find(t => t.themeName === themeName);
        return isDefaultTheme(found);
    };

    // 테마 목록 - template의 categories 사용
    const themes = React.useMemo(() => {
        return template?.categories || [];
    }, [template]);

    // 선택된 테마의 카테고리 목록
    const categories = React.useMemo(() => {
        // 신규 테마가 선택된 경우
        if (selectedNewTheme) {
            const newTheme = newThemes.find(t => t.themeName === selectedNewTheme);
            if (!newTheme) return [];
            
            // 기본 5개 카테고리 정의
            const defaultCategories = ['십성', '십이신살', '십이운성', '지지', '천간'];
            
            const categoryMap = new Map<string, any[]>();
            
            // 기본 카테고리들을 빈 배열로 초기화
            defaultCategories.forEach(catName => {
                categoryMap.set(catName, []);
            });
            
            // 업로드된 이미지가 있으면 해당 카테고리에 추가
            newTheme.images.forEach(image => {
                // uploadedImagesInfo에서 최신 카테고리 정보 가져오기
                const uniqueKey = `${selectedNewTheme}_${image.categoryName}_${image.cssName}`;
                const updatedInfo = uploadedImagesInfo.get(uniqueKey);
                const catName = updatedInfo?.categoryName || image.categoryName || '';
                
                if (catName) {
                    if (!categoryMap.has(catName)) {
                        categoryMap.set(catName, []);
                    }
                    categoryMap.get(catName)?.push({
                        ...image,
                        categoryName: catName
                    });
                }
            });
            
            // uploadedImagesInfo에 있는 이미지들도 추가 (아직 newTheme.images에 반영되지 않은 것들)
            uploadedImagesInfo.forEach((info, key) => {
                if (key.startsWith(`${selectedNewTheme}_`)) {
                    const catName = info.categoryName;
                    // 더미 데이터는 제외하고 실제 이미지만 처리
                    if (catName && !info.isDummy && !categoryMap.get(catName)?.some(img => img.cssName === info.cssName)) {
                        if (!categoryMap.has(catName)) {
                            categoryMap.set(catName, []);
                        }
                        // 임시 이미지 객체 생성
                        categoryMap.get(catName)?.push({
                            id: `temp_${info.cssName}`,
                            cssName: info.cssName,
                            categoryName: catName,
                            file: {
                                fileFullUrl: info.url
                            },
                            width: info.width,
                            height: info.height
                        });
                    } else if (info.isDummy) {
                        // 더미 데이터인 경우 빈 카테고리 생성만 수행
                        if (!categoryMap.has(catName)) {
                            categoryMap.set(catName, []);
                        }
                    }
                }
            });
            
            return Array.from(categoryMap.entries()).map(([name, images]) => ({
                name,
                images
            }));
        }
        
        // 기존 테마가 선택된 경우
        if (!selectedThemeName || !template?.categories) return [];
        
        const selectedTheme = template.categories.find(theme => theme.themeName === selectedThemeName);
        if (!selectedTheme) return [];
        
        const categoryMap = new Map<string, CssImage[]>();
        
        selectedTheme.images.forEach(image => {
            // uploadedImagesInfo에서 업데이트된 카테고리 정보 확인 (기존 호환: theme_cssName 키)
            const legacyKey = `${selectedThemeName}_${image.cssName}`;
            const legacyInfo = uploadedImagesInfo.get(legacyKey);
            const catNameFromLegacy = legacyInfo?.categoryName;
            const catName = catNameFromLegacy || image.categoryName;
            
            if (catName && !categoryMap.has(catName)) {
                categoryMap.set(catName, []);
            }
            if (catName) {
                categoryMap.get(catName)?.push({
                    ...image,
                    categoryName: catName
                });
            }
        });

        // uploadedImagesInfo에 있는 신규 이미지/더미 카테고리도 표시 (아직 template에 없는 것들)
        uploadedImagesInfo.forEach((info, key) => {
            if (key.startsWith(`${selectedThemeName}_`)) {
                const catName = info.categoryName;
                if (!catName) return;
                if (!categoryMap.has(catName)) categoryMap.set(catName, []);
                if (info?.isDummy) {
                    // 더미 데이터는 빈 카테고리만 유지
                    return;
                }
                const imagesInCat = categoryMap.get(catName)!;
                // 동일 cssName 없을 때만 추가 (임시 프리뷰)
                const exists = imagesInCat.some(img => img.cssName === info.cssName);
                if (!exists) {
                    imagesInCat.push({
                        id: `temp_${info.cssName}`,
                        cssName: info.cssName,
                        categoryName: catName,
                        file: {
                            fileFullUrl: info.url
                        },
                        width: info.width,
                        height: info.height
                    } as unknown as CssImage);
                }
            }
        });
        
        return Array.from(categoryMap.entries()).map(([name, images]) => ({
            name,
            images
        }));
    }, [selectedThemeName, selectedNewTheme, template, newThemes, uploadedImagesInfo]);

    const handleLoadTheme = async (e: React.ChangeEvent<HTMLSelectElement>) => {
        const selectedProductIdToLoad = e.target.value;
        if (!selectedProductIdToLoad) {
            return;
        }

        if (!window.confirm("다른 테마를 불러와 현재 테마의 이미지들을 덮어쓰시겠습니까? 이 작업은 되돌릴 수 없습니다.")) {
            e.target.value = "";
            return;
        }

        setLoading(true);
        try {
            const loadedTemplate = await getCssTemplate(selectedProductIdToLoad);
            if (loadedTemplate && template) { // 현재 template이 존재해야 병합 가능
                const newCreateList: CssImageCreate[] = [];
                const newUploadedImagesInfo = new Map(uploadedImagesInfo);
                const newUploadedImages = new Map(uploadedImages);

                // 현재 템플릿의 카테고리들을 복사
                const updatedCategories = template.categories ? [...template.categories] : [];

                // 불러온 템플릿의 각 테마와 이미지를 순회하며 현재 템플릿에 병합
                loadedTemplate.categories?.forEach(loadedTheme => {
                    // updatedCategories에서 현재 loadedTheme과 동일한 themeName을 가진 테마를 찾음
                    const targetCurrentTheme = updatedCategories.find(ct => normalizeName(ct.themeName) === normalizeName(loadedTheme.themeName));

                    if (targetCurrentTheme) { // 동일한 테마를 찾았다면
                        loadedTheme.images.forEach(loadedImage => {
                            // targetCurrentTheme 내에서만 이미지를 비교
                            targetCurrentTheme.images.forEach(currentImage => {
                                if (normalizeName(currentImage.categoryName) === normalizeName(loadedImage.categoryName) &&
                                    normalizeName(currentImage.cssName) === normalizeName(loadedImage.cssName)) {
                                    // 찾았다면, 해당 이미지의 file 정보를 업데이트
                                    currentImage.file = loadedImage.file;
                                    currentImage.width = loadedImage.width;
                                    currentImage.height = loadedImage.height;

                                    // createList에 추가하여 변경 사항 추적
                                    newCreateList.push({
                                        cssName: currentImage.cssName,
                                        file: loadedImage.file,
                                        themeName: targetCurrentTheme.themeName, // 현재 테마의 이름을 사용
                                        categoryName: currentImage.categoryName,
                                        width: currentImage.width || 0,
                                        height: currentImage.height || 0,
                                        imageId: currentImage.id // 기존 이미지 ID 유지
                                    });

                                    // uploadedImagesInfo 및 uploadedImages 업데이트 (프리뷰용)
                                    const uniqueKey = `${targetCurrentTheme.themeName}_${currentImage.categoryName}_${currentImage.cssName}`;
                                    newUploadedImagesInfo.set(uniqueKey, {
                                        cssName: currentImage.cssName,
                                        categoryName: currentImage.categoryName,
                                        themeName: targetCurrentTheme.themeName,
                                        url: loadedImage.file.fileFullUrl,
                                        fileId: loadedImage.file.id,
                                        width: currentImage.width,
                                        height: currentImage.height
                                    });
                                    newUploadedImages.set(uniqueKey, loadedImage.file.fileFullUrl);
                                }
                            });
                        });
                    }
                });

                // 업데이트된 카테고리들로 template 상태 업데이트
                setTemplate(prevTemplate => {
                    if (!prevTemplate) return undefined;
                    return {
                        ...prevTemplate,
                        categories: updatedCategories,
                    };
                });

                setCreateList(newCreateList);
                setUploadedImagesInfo(newUploadedImagesInfo);
                setUploadedImages(newUploadedImages);
                setDeleteCssNames(new Set()); // 불러온 테마로 덮어쓰므로 삭제 목록 초기화

                const mainTheme = loadedTemplate.categories?.find(theme => theme.isMain);
                if (mainTheme) {
                    setSelectedMainTheme(mainTheme.themeName);
                } else {
                    const defaultMain = loadedTemplate.categories?.find(t => isDefaultTheme(t));
                    setSelectedMainTheme(defaultMain ? defaultMain.themeName : null);
                }

                handleCloseSidebar(); // 사이드바 닫기

                toast.success(`'${e.target.options[e.target.selectedIndex].text}' 테마의 이미지들을 불러와 현재 테마에 덮어쓰기했습니다. '저장하기'를 눌러 변경사항을 적용하세요.`);
            } else if (!template) {
                toast.error("현재 테마 정보를 불러오지 못했습니다. 먼저 현재 테마를 로드해주세요.");
            }
        } catch (error) {
            console.error("Failed to load theme", error);
            toast.error("테마를 불러오는데 실패했습니다.");
        } finally {
            setLoading(false);
            e.target.value = "";
        }
    };

    // 업로드 중간에 참조가 바뀌면 진행 중 작업이 끊어지기 때문에 files와 template 중심으로만 트리거한다.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => {
        if (isOpen) {
            const fetchTemplate = async () => {
                setLoading(true);
                const templateImages = await getCssTemplate(productId);
                setTemplate(templateImages);

                // isMain이 true인 테마를 자동으로 선택
                const mainTheme = templateImages?.categories?.find(theme => theme.isMain);
                if (mainTheme) {
                    setSelectedMainTheme(mainTheme.themeName);
                } else {
                    // 백엔드 보장 실패 시, 기본 테마(테마 없음)를 메인으로 설정
                    const defaultMain = templateImages?.categories?.find(t => isDefaultTheme(t));
                    if (defaultMain) setSelectedMainTheme(defaultMain.themeName);
                }

                setLoading(false);
            };
            fetchTemplate();
            if (role === 'ROLE_USER' || role === 'ROLE_MARKETING') {
                const fetchAvailableThemes = async () => {
                    try {
                        const response = await apiClient.get<ThemeInfo[]>('/css/img');
                        setAvailableThemes(response.data.filter(theme => theme.productId !== productId));
                    } catch (error) {
                        console.error("Failed to fetch available themes", error);
                        toast.error("다른 테마 목록을 불러오는데 실패했습니다.");
                    }
                };
                fetchAvailableThemes();
            }
        } else {
            // 모달이 닫힐 때 데이터 초기화
            setTemplate(undefined);
            setSelectedThemeName(null);
            setShowSidebar(false);
            setSelectedMainTheme(null);
            setFiles([]);
            setSaveList([]);
            setCreateList([]);
            setUploadedImages(new Map());
            setUploadedImagesInfo(new Map());
            setShowNewThemeInput(false);
            setNewThemeName('');
            setNewThemes([]);
            setSelectedNewTheme(null);
            setAvailableThemes([]);
        }
    }, [getCssTemplate, isOpen, templateId, productId, role]);

    // 테마 클릭 핸들러
    const handleThemeClick = (themeName: string) => {
        setSelectedThemeName(themeName);
        setSelectedNewTheme(null);
        setShowSidebar(true);
    };

    // 사이드바 닫기 핸들러
    const handleCloseSidebar = () => {
        setShowSidebar(false);
        setSelectedThemeName(null);
        setSelectedNewTheme(null);
    };

    // 메인 테마 선택 핸들러 (라디오 선택 - 하나만 선택 가능)
    const handleMainThemeSelect = (themeName: string) => {
        setSelectedMainTheme(themeName);
    };

    const handleRequestNewThemeInput = () => {
        setShowNewThemeInput(true);
    };

    const handleNewThemeNameChange = (value: string) => {
        setNewThemeName(value);
    };

    const handleCancelNewThemeInput = () => {
        setShowNewThemeInput(false);
        setNewThemeName('');
    };

    const handleAddNewTheme = () => {
        const rawName = newThemeName;
        const normalized = normalizeName(rawName);
        if (!normalized) {
            toast.warning('테마 이름을 입력해주세요.');
            return;
        }

        const existingThemeNames = new Set<string>([
            ...((template?.categories || []).map(t => normalizeName(t.themeName))),
            ...(newThemes.map(t => normalizeName(t.themeName)))
        ]);

        if (existingThemeNames.has(normalized)) {
            toast.warning(`"${rawName.trim()}" 테마명이 이미 존재합니다.`);
            return;
        }

        if (!rawName.trim()) {
            return;
        }

        const hasExistingMain = Boolean(selectedMainTheme);
        const defaultCategories = ['십성', '십이신살', '십이운성', '지지', '천간'];
        const newTheme: ThemeCreate = {
            themeName: rawName.trim(),
            isMain: false,
            images: []
        };

        setNewThemes(prev => [...prev, newTheme]);
        setNewThemeName('');
        setShowNewThemeInput(false);
        if (!hasExistingMain) {
            setSelectedMainTheme(rawName.trim());
        }

        toast.success(`테마 "${rawName.trim()}"이 기본 5개 카테고리(${defaultCategories.join(', ')})와 함께 추가되었습니다.`);
    };

    const handleOpenNewTheme = (themeName: string) => {
        setSelectedNewTheme(themeName);
        setSelectedThemeName(null);
        setShowSidebar(true);
    };

    const handleShowNewCategoryInput = () => {
        setShowNewCategoryInput(true);
    };

    const handleNewCategoryNameChange = (value: string) => {
        setNewCategoryName(value);
    };

    const handleCancelNewCategoryInput = () => {
        setShowNewCategoryInput(false);
        setNewCategoryName('');
    };

    const handleSubmitNewCategory = () => {
        const rawCategory = newCategoryName;
        const normalizedCategory = normalizeName(rawCategory);
        if (!normalizedCategory) {
            toast.warning('카테고리 이름을 입력해주세요.');
            return;
        }

        const categoryName = rawCategory.trim();
        const isDuplicateInView = categories.some(c => normalizeName(c.name) === normalizedCategory);
        if (isDuplicateInView) {
            toast.warning(`"${categoryName}" 카테고리가 이미 존재합니다.`);
            return;
        }

        if (!categoryName) {
            return;
        }

        if (isDefaultThemeByName(selectedThemeName)) {
            toast.error('"테마 없음"에는 카테고리를 추가할 수 없습니다.');
            return;
        }

        let created = false;

        if (selectedNewTheme) {
            const dummyKey = `${selectedNewTheme}_${categoryName}_dummy`;
            const newUploadedImagesInfo = new Map(uploadedImagesInfo);
            const categoryExists = Array.from(uploadedImagesInfo.entries()).some(([key, info]) => (
                key.startsWith(`${selectedNewTheme}_`) && normalizeName(info?.categoryName || '') === normalizedCategory
            ));

            if (categoryExists) {
                toast.warning(`"${categoryName}" 카테고리가 이미 존재합니다.`);
                return;
            }

            newUploadedImagesInfo.set(dummyKey, {
                cssName: 'dummy',
                categoryName,
                themeName: selectedNewTheme,
                url: '',
                fileId: '',
                width: 0,
                height: 0,
                isDummy: true
            });

            setUploadedImagesInfo(newUploadedImagesInfo);
            toast.success(`"${categoryName}" 카테고리가 추가되었습니다. 이미지를 업로드해주세요.`);
            created = true;
        } else if (selectedThemeName) {
            const dummyKey = `${selectedThemeName}_${categoryName}_dummy`;
            const newUploadedImagesInfo = new Map(uploadedImagesInfo);
            const categoryExists = Array.from(uploadedImagesInfo.entries()).some(([key, info]) => (
                key.startsWith(`${selectedThemeName}_`) && normalizeName(info?.categoryName || '') === normalizedCategory
            ));

            if (categoryExists) {
                toast.warning(`"${categoryName}" 카테고리가 이미 존재합니다.`);
                return;
            }

            newUploadedImagesInfo.set(dummyKey, {
                cssName: 'dummy',
                categoryName,
                themeName: selectedThemeName,
                url: '',
                fileId: '',
                width: 0,
                height: 0,
                isDummy: true
            });

            setUploadedImagesInfo(newUploadedImagesInfo);
            toast.success(`"${categoryName}" 카테고리가 추가되었습니다. 이미지를 업로드해주세요.`);
            created = true;
        }

        if (created) {
            setNewCategoryName('');
            setShowNewCategoryInput(false);
        }
    };

    const handleUploadFiles = (filesWithCategory: File[]) => {
        setFiles(filesWithCategory);
    };

    // 공통 비율 확인 함수
    // 업로드 처리 중 참조가 계속 바뀌면 진행 중 작업이 끊어지므로 files 변화에만 반응시킨다.
    useEffect(() => {
        const uploadFiles = async () => {
            if (files.length > 0) {
                setIsUploading(true); // 업로드 시작
                const newSaveList: CssImageRequest[] = [];
                const newUploadedImages = new Map(uploadedImages);
                const newUploadedImagesInfo = new Map(uploadedImagesInfo);

                for (const file of files) {
                    // 파일 객체와 name 속성 유효성 검사
                    if (!file || !file.name) {
                        console.error('Invalid file object:', file);
                        continue;
                    }

                    // 기본 테마에는 업로드 불가 (일반/관리자 모두)
                    if (isDefaultThemeByName(selectedThemeName)) {
                        toast.warning('"테마 없음"에는 이미지를 업로드할 수 없습니다.');
                        continue;
                    }

                    // 파일명에서 확장자 제거하여 cssName 추출
                    const fileNameWithoutExt = file.name.replace(/\.[^/.]+$/, "");

                    // macOS의 NFD 형태를 NFC로 정규화 (한글 자모 결합)
                    const normalizedFileName = fileNameWithoutExt.normalize('NFC').trim();

                    // 카테고리별 업로드에서 카테고리 정보 가져오기
                    const currentCategoryName = (file as any).categoryName || '';

                    // 신규 테마에 업로드하는 경우
                    if (selectedNewTheme) {
                        const currentTheme = newThemes.find(t => t.themeName === selectedNewTheme);
                        if (currentTheme) {
                            // categoryName + cssName으로 중복 체크 (신규 테마에서는 categoryName이 다르면 같은 cssName 허용)
                            const existingImage = currentTheme.images.find(
                                img => img.cssName.toLowerCase() === normalizedFileName.toLowerCase() &&
                                       img.categoryName === currentCategoryName
                            );

                            try {
                                // 이미지 크기 가져오기
                                const imageDimensions = await new Promise<{width: number, height: number, ratioType: string}>((resolve) => {
                                    const img = new Image();
                                    const url = URL.createObjectURL(file);

                                    img.onload = () => {
                                        const ratioType = getAspectRatioType(img.width, img.height);
                                        const dimensions = {
                                            width: img.width,
                                            height: img.height,
                                            ratioType: ratioType
                                        };
                                        URL.revokeObjectURL(url);
                                        resolve(dimensions);
                                    };

                                    img.onerror = () => {
                                        URL.revokeObjectURL(url);
                                        resolve({ width: 0, height: 0, ratioType: 'unknown' });
                                    };

                                    img.src = url;
                                });

                                const formData = new FormData();
                                formData.append('file', file);
                                const response = await apiClient.post('/upload-file/upload', formData);

                                // 업로드 미리보기/정보 키
                                const uniqueKey = `${selectedNewTheme}_${currentCategoryName}_${normalizedFileName}`;
                                const imageUrl = URL.createObjectURL(file);
                                newUploadedImages.set(uniqueKey, imageUrl);
                                newUploadedImagesInfo.set(uniqueKey, {
                                    cssName: normalizedFileName,
                                    categoryName: currentCategoryName,
                                    themeName: selectedNewTheme,
                                    url: imageUrl,
                                    fileId: response.data.id,
                                    width: imageDimensions.width,
                                    height: imageDimensions.height
                                });

                                if (existingImage) {
                                    // 존재하면 교체
                                    setNewThemes(prevThemes => prevThemes.map(t => {
                                        if (t.themeName !== selectedNewTheme) return t;
                                        return {
                                            ...t,
                                            images: t.images.map(img => {
                                                const same = img.cssName.toLowerCase() === normalizedFileName.toLowerCase() && (img.categoryName || '') === currentCategoryName;
                                                return same ? { ...img, fileId: response.data.id, width: imageDimensions.width, height: imageDimensions.height, file: response.data } as any : img;
                                            })
                                        };
                                    }));
                                    toast.success(`"${currentCategoryName}" 카테고리의 "${normalizedFileName}" 이미지를 교체했습니다.`);
                                } else {
                                    // 없으면 추가
                                    const newImage: ImageCreate = {
                                        cssName: normalizedFileName,
                                        categoryName: currentCategoryName,
                                        fileId: response.data.id,
                                        width: imageDimensions.width,
                                        height: imageDimensions.height
                                    };
                                    const imageWithPreview = { ...newImage, file: response.data } as any;
                                    setNewThemes(prevThemes => prevThemes.map(t => {
                                        if (t.themeName === selectedNewTheme) {
                                            return { ...t, images: [...t.images, imageWithPreview] };
                                        }
                                        return t;
                                    }));
                                }

                            } catch (error) {
                                console.error('이미지 업로드 실패:', error);
                                toast.error(`"${file.name}" 업로드 실패`);
                            }
                            continue;
                        }
                    }

                    // 기존 테마에 업로드하는 경우
                    const matchingImages: CssImage[] = [];
                    if (selectedThemeName && template?.categories) {
                        // 현재 선택된 테마 내에서만 매칭되는 이미지 찾기
                        const selectedTheme = template.categories.find(theme => theme.themeName === selectedThemeName);
                        if (selectedTheme) {
                            selectedTheme.images.forEach(img => {
                                const categoryNameCleaned = img.categoryName.toLowerCase();
                                const currentCategoryCleaned = currentCategoryName.toLowerCase();
                                const cssNameCleaned = img.cssName.trim().toLowerCase();
                                const fileNameCleaned = normalizedFileName.toLowerCase();
                                if (cssNameCleaned === fileNameCleaned && categoryNameCleaned === currentCategoryCleaned) {
                                    matchingImages.push(img);
                                }
                            });
                        }
                    }

                    // cssName이 매칭되는 경우 - 기존 이미지 수정으로 처리
                    if (matchingImages.length > 0) {
                        // 비율 체크가 필요한 경우
                    let skipUpload = false;
                    for (const matchingImage of matchingImages) {
                        if (matchingImage.width && matchingImage.height) {
                            // 이미지 비율 체크를 위해 Promise 사용
                            const isValidRatio = await new Promise<boolean>((resolve) => {
                                const upload = new Image();
                                const url = URL.createObjectURL(file);

                                upload.onload = () => {
                                    const uploadRatioType = getAspectRatioType(upload.width, upload.height);
                                    const originRatioType = getAspectRatioType(matchingImage.width!, matchingImage.height!);

                                    if (uploadRatioType !== originRatioType) {
                                        toast.error(`"${matchingImage.categoryName}-${matchingImage.cssName}"은 ${originRatioType} 비율만 등록 가능합니다.`);
                                        resolve(false);
                                    } else {
                                        resolve(true);
                                    }
                                    URL.revokeObjectURL(url);
                                };

                                upload.onerror = () => {
                                    URL.revokeObjectURL(url);
                                    resolve(false);
                                };

                                upload.src = url;
                            });

                            if (!isValidRatio) {
                                skipUpload = true;
                                break;
                            }
                        }
                    }

                    if (skipUpload) {
                        continue; // 다음 파일로 진행
                    }

                    // 파일 업로드
                    const formData = new FormData();
                    formData.append('file', file);

                    try {
                        const response = await apiClient.post('/upload-file/upload', formData);
                        const imageUrl = URL.createObjectURL(file);

                        // 매칭되는 모든 이미지에 대해 처리 - createList에 추가하여 동일하게 관리
                        for (const matchingImage of matchingImages) {
                            // 기존 이미지 수정을 createList에 추가
                            const createRequest: CssImageCreate = {
                                cssName: matchingImage.cssName,
                                file: response.data,
                                themeName: selectedThemeName || '', // 현재 선택된 테마명 사용
                                categoryName: currentCategoryName || matchingImage.categoryName || '', // 카테고리별 업로드에서 가져온 카테고리 우선 사용
                                width: matchingImage.width || 0,
                                height: matchingImage.height || 0,
                                imageId: matchingImage.id // 기존 이미지 ID 포함
                            };

                            // 테마명, 카테고리명, cssName을 조합하여 고유 키 생성
                            const uniqueKey = `${selectedThemeName}_${matchingImage.categoryName}_${matchingImage.cssName}`;

                            // createList에 추가하여 신규 이미지와 동일하게 관리
                            setCreateList(prev => {
                                // 중복 방지를 위해 기존 항목 제거 후 추가 - categoryName+cssName으로 비교
                                const filtered = prev.filter(item =>
                                    !(item.cssName === matchingImage.cssName &&
                                      item.categoryName === matchingImage.categoryName &&
                                      item.themeName === matchingImage.themeName)
                                );
                                return [...filtered, createRequest];
                            });

                            newUploadedImages.set(uniqueKey, imageUrl);
                        }
                    } catch (error) {
                        console.error('이미지 업로드 실패:', error);
                        toast.error(`"${file.name}" 업로드 실패`);
                    }
                    } else {
                        // cssName이 매칭되지 않는 경우 - 신규 이미지 등록 (관리자만)
                        if (role === 'ROLE_ADMIN') {
                            try {
                                // 이미지 크기 가져오기
                                const imageDimensions = await new Promise<{width: number, height: number, ratioType: string}>((resolve) => {
                                    const img = new Image();
                                    const url = URL.createObjectURL(file);

                                    img.onload = () => {
                                        const ratioType = getAspectRatioType(img.width, img.height);
                                        const dimensions = {
                                            width: img.width,
                                            height: img.height,
                                            ratioType: ratioType
                                        };
                                        URL.revokeObjectURL(url);
                                        resolve(dimensions);
                                    };

                                    img.onerror = () => {
                                        URL.revokeObjectURL(url);
                                        resolve({ width: 0, height: 0, ratioType: 'unknown' }); // 에러 시 기본값
                                    };

                                    img.src = url;
                                });

                                const formData = new FormData();
                                formData.append('file', file);
                                const response = await apiClient.post('/upload-file/upload', formData);

                                // 신규 이미지 요청 생성 (실제 이미지 크기 포함)
                                const request: CssImageCreate = {
                                    cssName: normalizedFileName,
                                    file: response.data,
                                    themeName: selectedThemeName || '',
                                    categoryName: currentCategoryName,
                                    width: imageDimensions.width,
                                    height: imageDimensions.height
                                };

                                setCreateList(prev => {
                                    const filtered = prev.filter(item => !(
                                        item.cssName === normalizedFileName &&
                                        (item.categoryName || '') === (currentCategoryName || '') &&
                                        (item.themeName || '') === (selectedThemeName || '')
                                    ));
                                    return [...filtered, request];
                                });
                                // 프리뷰 키를 테마/카테고리/cssName 기준으로 저장
                                newUploadedImages.set(`${selectedThemeName}_${currentCategoryName}_${normalizedFileName}`, URL.createObjectURL(file));

                                // 신규 이미지 등록 시에도 uploadedImagesInfo에 저장
                                const uniqueKey = `${selectedThemeName}_${currentCategoryName}_${normalizedFileName}`;
                                const imageUrl = URL.createObjectURL(file);
                                newUploadedImagesInfo.set(uniqueKey, {
                                    cssName: normalizedFileName,
                                    categoryName: currentCategoryName, // 카테고리 입력값 사용
                                    themeName: selectedThemeName,
                                    url: imageUrl,
                                    fileId: response.data.id,
                                    width: imageDimensions.width,
                                    height: imageDimensions.height
                                });

                            } catch (error) {
                                console.error('이미지 업로드 실패:', error);
                                toast.error(`"${file.name}" 업로드 실패`);
                            }
                        } else {
                            toast.warning(`${currentCategoryName} 카테고리에 파일명 "${normalizedFileName}"과 일치하는 항목을 찾을 수 없습니다.`);
                        }
                    }
                }

                // 상태 업데이트 - 테마별로 관리
                if (newSaveList.length > 0) {
                    setSaveList(prev => {
                        const updatedList = [...prev];
                        newSaveList.forEach(newItem => {
                            // 같은 테마의 같은 cssName만 교체
                            const existingIndex = updatedList.findIndex(item =>
                                item.cssName === newItem.cssName &&
                                item.themeName === newItem.themeName
                            );
                            if (existingIndex >= 0) {
                                updatedList[existingIndex] = newItem;
                            } else {
                                updatedList.push(newItem);
                            }
                        });
                        return updatedList;
                    });
                }

                // uploadedImages는 신규 이미지도 포함하므로 별도로 업데이트
                if (newUploadedImages.size > uploadedImages.size) {
                    setUploadedImages(newUploadedImages);
                }

                if (newUploadedImagesInfo.size > uploadedImagesInfo.size) {
                    setUploadedImagesInfo(newUploadedImagesInfo);
                }

                setFiles([]); // 업로드 후 파일 초기화
                setIsUploading(false); // 업로드 종료
            }
        };

        uploadFiles();
    }, [files, template]); // eslint-disable-line react-hooks/exhaustive-deps

    // 기본 이미지로 복구하는 함수
    const handleRestoreDefault = async (image: any) => {
        if (!image.originFullFilePath || !image.originFileId) {
            toast.error('기본 이미지 정보가 없습니다.');
            return;
        }

        try {
            // deleteCssNames에 themeName_cssName 형식으로 추가
            setDeleteCssNames((prev: Set<string>) => {
                const newSet = new Set(prev);
                // 현재 테마와 cssName을 조합하여 저장
                const deleteKey = makeDeleteKey((selectedThemeName || selectedNewTheme || ''), (image.categoryName || ''), image.cssName);
                newSet.add(deleteKey);
                return newSet;
            });

            // 미리보기를 위해 template의 이미지 URL 업데이트
            setTemplate(prev => {
                if (!prev) return prev;
                const themeName = (selectedThemeName || selectedNewTheme || '');
                return {
                    ...prev,
                    categories: prev.categories?.map(theme => {
                        if (theme.themeName !== themeName) return theme;
                        const images = theme.images.map(item => {
                            const same = item.cssName === image.cssName && ((item.categoryName || '') === (image.categoryName || ''));
                            if (!same) return item;
                            return {
                                ...item,
                                file: {
                                    ...item.file,
                                    id: image.originFileId,
                                    fileFullUrl: image.originFullFilePath,
                                    fileName: image.originFileName
                                }
                            };
                        });
                        return { ...theme, images };
                    })
                };
            });

            // uploadedImages에서 해당 이미지 제거 (원본 이미지로 표시되도록)
            const themeName = selectedThemeName || selectedNewTheme;
            const legacyKey = `${themeName}_${image.cssName}`;
            const compositeKey = `${themeName}_${image.categoryName || ''}_${image.cssName}`;

            setUploadedImages(prev => {
                const newMap = new Map(prev);
                newMap.delete(legacyKey);
                newMap.delete(compositeKey);
                return newMap;
            });

            // uploadedImagesInfo에서도 제거
            setUploadedImagesInfo(prev => {
                const newMap = new Map(prev);
                newMap.delete(legacyKey);
                newMap.delete(compositeKey);
                return newMap;
            });
            
            // createList에서도 해당 이미지 제거 (중복 방지)
            setCreateList(prev => prev.filter(item => 
                !(item.cssName === image.cssName && 
                  item.themeName === (selectedThemeName || selectedNewTheme) &&
                  (item.categoryName || '') === (image.categoryName || ''))
            ));

            toast.success(`저장하기 버튼을 누르면 "${image.cssName}" 이미지가 기본 이미지로 복구됩니다.`);
        } catch (error) {
            console.error('기본 이미지 복구 실패:', error);
            toast.error('기본 이미지 복구에 실패했습니다.');
        }
    };

    if (!isOpen) return null;

    const originalMainTheme = template?.categories?.find(theme => theme.isMain)?.themeName;
    const isMainThemeChanged = selectedMainTheme !== originalMainTheme;
    const hasNewThemeImages = newThemes.some(t => t.images.length > 0);
    const hasImageChanges = (saveList.length > 0) || (createList.length > 0) || (deleteCssNames.size > 0);
    const hasAnyChanges = hasNewThemeImages || hasImageChanges || isMainThemeChanged;
    const isDisabled = !hasAnyChanges || isUploading;

    const handleSaveClick = async () => {
        // 변경사항 감지: 신규 이미지/교체/삭제/메인 변경 여부
        const originalMainThemeName = template?.categories?.find(theme => theme.isMain)?.themeName;
        const isMainThemeChangedLocal = selectedMainTheme !== originalMainThemeName;
        const hasNewThemeImagesLocal = newThemes.some(t => t.images.length > 0);
        const hasImageChangesLocal = (saveList.length > 0) || (createList.length > 0) || (deleteCssNames.size > 0);
        const hasAnyChangesLocal = hasNewThemeImagesLocal || hasImageChangesLocal || isMainThemeChangedLocal;

        if (!hasAnyChangesLocal) {
            alert('변경사항이 없습니다.');
            return;
        }

        // isMain만 변경된 경우: 즉시 반영 후 종료
        if (!hasNewThemeImagesLocal && !hasImageChangesLocal && isMainThemeChangedLocal) {
            try {
                if (role === 'ROLE_ADMIN') {
                    await saveBasicCssTemplate([], productId, selectedMainTheme, template?.categories);
                } else {
                    const originalMain = template?.categories?.find(t => t.isMain);
                    const selectedMain = template?.categories?.find(t => t.themeName === selectedMainTheme);
                    const updates: Array<{ themeId: string; isMain: boolean; images: Array<{ imageId?: string; fileId?: string }> }> = [];
                    if (selectedMain?.themeId) {
                        if (originalMain?.themeId && originalMain.themeId !== selectedMain.themeId) {
                            updates.push({ themeId: originalMain.themeId!, isMain: false, images: [] });
                            updates.push({ themeId: selectedMain.themeId!, isMain: true, images: [] });
                        } else if (!originalMain?.themeId) {
                            updates.push({ themeId: selectedMain.themeId!, isMain: true, images: [] });
                        }
                    }
                    if (updates.length > 0) {
                        await updateThemesForUser(updates, productId);
                    }
                }
                toast.success('메인 테마가 변경되었습니다.');
                onClose();
            } catch (e) {
                toast.error('메인 테마 변경에 실패했습니다.');
                console.error(e);
            }
            return;
        }

        // 신규 테마 처리 (신규 이미지가 있는 경우)
        if (hasNewThemeImagesLocal) {
            try {
                // BasicCreate 구조로 변환
                const basicCreate: BasicCreate = {
                    themes: newThemes.map(theme => {
                        const mappedImages = theme.images.map(img => {
                            // uploadedImagesInfo에서 수정된 카테고리 정보 가져오기
                            const uniqueKey = `${theme.themeName}_${img.cssName}`;
                            const updatedInfo = uploadedImagesInfo.get(uniqueKey);

                            return {
                                fileId: img.fileId,
                                categoryName: updatedInfo?.categoryName || img.categoryName || '',
                                cssName: img.cssName,
                                width: img.width,
                                height: img.height
                            };
                        });

                        return {
                            themeName: theme.themeName,
                            isMain: theme.themeName === selectedMainTheme,
                            images: mappedImages
                        };
                    })
                };

                await saveNewThemes(basicCreate, productId);
                // 신규 테마 저장 후 메인 유일성 보장: 최신 템플릿 재조회 후 메인 반영
                try {
                    const refreshed = await getCssTemplate(productId);
                    // 선택된 메인만 true, 나머지는 false가 되도록 서버에 반영
                    await saveBasicCssTemplate([], productId, selectedMainTheme, refreshed?.categories);
                } catch (e) {
                    console.warn('메인 테마 유일성 반영 중 경고:', e);
                }
                toast.success('신규 테마가 저장되었습니다.');
                onClose();
                return;
            } catch (error) {
                toast.error('테마 저장에 실패했습니다.');
                console.error(error);
                return;
            }
        }

        if (hasAnyChangesLocal) {
            try {
                // 신규 이미지 등록 (관리자만) 또는 메인 테마 변경만 있는 경우
                if (role === 'ROLE_ADMIN' && (createList.length > 0 || saveList.length > 0 || uploadedImagesInfo.size > 0 || isMainThemeChangedLocal)) {
                    if (isMainThemeChangedLocal && createList.length === 0 && saveList.length === 0) {
                        // 메인만 변경: 빈 이미지로 기존 테마 정보와 함께 메인만 반영
                        await saveBasicCssTemplate([], productId, selectedMainTheme, template?.categories);
                    } else {
                        // 이미지 변경이 있는 경우
                        // createList 중복 제거: (themeName, categoryName, cssName) 키로 마지막 항목만 유지
                        const imageMap = new Map<string, CssImageCreateRequest>();

                        // 복구 항목(원본 파일로) 먼저 반영
                        deleteCssNames.forEach(deleteKey => {
                            const { themeName, categoryName, cssName } = parseDeleteKey(deleteKey as any);
                            const theme = template?.categories?.find(t => t.themeName === themeName);
                            const img = theme?.images.find(i => i.cssName === cssName && ((i.categoryName || '') === (categoryName || '')));
                            if (img && img.originFileId) {
                                const req: CssImageCreateRequest = {
                                    imageId: img.id,
                                    cssName: img.cssName,
                                    themeName,
                                    categoryName: img.categoryName || '',
                                    fileId: img.originFileId,
                                    width: img.width || 0,
                                    height: img.height || 0
                                };
                                const dedupeKey = `${req.themeName}__${req.categoryName}__${req.cssName}`;
                                imageMap.set(dedupeKey, req);
                            }
                        });

                        createList.forEach(item => {
                            const compositeKeyForInfo = `${item.themeName}_${item.categoryName || ''}_${item.cssName}`;
                            const legacyKeyForInfo = `${item.themeName}_${item.cssName}`;
                            const uploadedInfo = uploadedImagesInfo.get(compositeKeyForInfo) || uploadedImagesInfo.get(legacyKeyForInfo);

                            const imageRequest: CssImageCreateRequest = {
                                cssName: item.cssName,
                                themeName: item.themeName,
                                categoryName: uploadedInfo?.categoryName || item.categoryName || '',
                                fileId: item.file.id,
                                width: item.width,
                                height: item.height
                            };

                            // 기존 이미지 수정인 경우 imageId 추가
                            if (item.imageId) {
                                imageRequest.imageId = item.imageId;
                            }
                            const dedupeKey = `${imageRequest.themeName}__${imageRequest.categoryName}__${imageRequest.cssName}`;
                            imageMap.set(dedupeKey, imageRequest);
                        });
                        const allImages: CssImageCreateRequest[] = Array.from(imageMap.values());

                        await saveBasicCssTemplate(allImages, productId, selectedMainTheme, template?.categories);
                    }
                } else if (role === 'ROLE_USER' || role === 'ROLE_MARKETING') {
                    // 일반 유저: 여러 테마를 업데이트 가능. 백엔드가 List<ThemeUpdate> 수신.
                    const updates: Array<{ themeId: string; isMain: boolean; images: Array<{ imageId?: string; fileId?: string }> }> = [];

                    const originalMain = template?.categories?.find(t => t.isMain);
                    const selectedMain = template?.categories?.find(t => t.themeName === selectedMainTheme);

                    // 테마별 이미지 업데이트 수집
                    template?.categories?.forEach(theme => {
                        if (!theme.themeId) return;
                        const imageUpdateMap = new Map<string, { imageId: string; fileId: string }>();

                        // 1) 복구 요청 반영
                        deleteCssNames.forEach(deleteKey => {
                            const { themeName, categoryName, cssName } = parseDeleteKey(deleteKey as any);
                            if (themeName === theme.themeName) {
                                const image = theme.images.find(img => img.cssName === cssName && ((img.categoryName || '') === (categoryName || '')));
                                if (image && image.originFileId) {
                                    imageUpdateMap.set(image.id, { imageId: image.id, fileId: image.originFileId });
                                }
                            }
                        });

                        // 2) 업로드(교체) 반영 - 기존 이미지에 대한 교체만 처리 (imageId가 있을 때)
                        createList.forEach(item => {
                            if (item.themeName === theme.themeName && item.imageId) {
                                imageUpdateMap.set(item.imageId, { imageId: item.imageId, fileId: item.file.id });
                            }
                        });

                        const images = Array.from(imageUpdateMap.values());
                        const isMain = theme.themeName === selectedMainTheme;

                        // 이미지 변경이 있거나 메인 변경 대상이면 업데이트 항목에 포함
                        if (images.length > 0 || (originalMain?.themeId && isMain !== (theme.themeId === originalMain.themeId))) {
                            updates.push({ themeId: theme.themeId, isMain, images });
                        }
                    });

                    // 메인만 바뀌고 이미지 변경이 전혀 없는 경우에도 반영 필요
                    if (updates.length === 0 && originalMain && selectedMain && originalMain.themeId !== selectedMain.themeId) {
                        updates.push({ themeId: originalMain.themeId!, isMain: false, images: [] });
                        updates.push({ themeId: selectedMain.themeId!, isMain: true, images: [] });
                    }

                    if (updates.length > 0) {
                        await updateThemesForUser(updates, productId);
                    }
                }

                toast.success('템플릿이 저장되었습니다.');
                onClose();
            } catch (error) {
                toast.error('템플릿 저장에 실패했습니다.');
                console.error(error);
            }
        } else {
            // 신규 테마 이미지가 없더라도, 이미지 교체/삭제 또는 메인 변경만 저장
            try {
                if (role === 'ROLE_ADMIN') {
                    // 관리자: 기본 템플릿 저장 또는 메인만 변경 시에도 저장
                    if (isMainThemeChangedLocal && createList.length === 0 && saveList.length === 0) {
                        // 메인만 변경은 기본 저장 API로 처리
                        await saveBasicCssTemplate([], productId, selectedMainTheme, template?.categories);
                    } else {
                        // 이미지 변경(교체/삭제) 포함
                        const imageMap = new Map<string, CssImageCreateRequest>();
                        // 삭제 복구 처리 -> originFileId로 되돌림
                        deleteCssNames.forEach(deleteKey => {
                            const { themeName, categoryName, cssName } = parseDeleteKey(deleteKey as any);
                            const theme = template?.categories?.find(t => t.themeName === themeName);
                            const image = theme?.images.find(img => img.cssName === cssName && ((img.categoryName || '') === (categoryName || '')));
                            if (image?.originFileId) {
                                const imageRequest: CssImageCreateRequest = {
                                    themeName: themeName,
                                    imageId: image.id,
                                    cssName: cssName,
                                    categoryName: categoryName,
                                    fileId: image.originFileId,
                                    width: image.width,
                                    height: image.height
                                } as any;
                                const dedupeKey = `${imageRequest.themeName}__${imageRequest.categoryName}__${imageRequest.cssName}`;
                                imageMap.set(dedupeKey, imageRequest);
                            }
                        });

                        // 교체 업로드 처리 (기존 이미지의 fileId 교체)
                        createList.forEach(item => {
                            if (item.imageId) {
                                const imageRequest: CssImageCreateRequest = {
                                    themeName: item.themeName,
                                    imageId: item.imageId,
                                    cssName: item.cssName,
                                    categoryName: item.categoryName,
                                    fileId: item.file.id,
                                    width: item.width,
                                    height: item.height
                                } as any;
                                const dedupeKey = `${imageRequest.themeName}__${imageRequest.categoryName}__${imageRequest.cssName}`;
                                imageMap.set(dedupeKey, imageRequest);
                            }
                        });

                        const allImages: CssImageCreateRequest[] = Array.from(imageMap.values());
                        await saveBasicCssTemplate(allImages, productId, selectedMainTheme, template?.categories);
                    }
                } else if (role === 'ROLE_USER' || role === 'ROLE_MARKETING') {
                    // 일반 유저: 이미지 변경이 없더라도 메인 변경만 반영
                    const updates: Array<{ themeId: string; isMain: boolean; images: Array<{ imageId?: string; fileId?: string }> }> = [];
                    const originalMain = template?.categories?.find(t => t.isMain);
                    const selectedMain = template?.categories?.find(t => t.themeName === selectedMainTheme);

                    template?.categories?.forEach(theme => {
                        if (!theme.themeId) return;
                        const imageUpdateMap = new Map<string, { imageId: string; fileId: string }>();

                        // 복구 처리
                        deleteCssNames.forEach(deleteKey => {
                            const { themeName, categoryName, cssName } = parseDeleteKey(deleteKey as any);
                            if (themeName === theme.themeName) {
                                const image = theme.images.find(img => img.cssName === cssName && ((img.categoryName || '') === (categoryName || '')));
                                if (image && image.originFileId) {
                                    imageUpdateMap.set(image.id, { imageId: image.id, fileId: image.originFileId });
                                }
                            }
                        });

                        // 교체 처리
                        createList.forEach(item => {
                            if (item.themeName === theme.themeName && item.imageId) {
                                imageUpdateMap.set(item.imageId, { imageId: item.imageId, fileId: item.file.id });
                            }
                        });

                        const images = Array.from(imageUpdateMap.values());
                        const isMain = theme.themeName === selectedMainTheme;
                        if (images.length > 0 || (originalMain?.themeId && isMain !== (theme.themeId === originalMain.themeId))) {
                            updates.push({ themeId: theme.themeId, isMain, images });
                        }
                    });

                    if (updates.length === 0 && originalMain && selectedMain && originalMain.themeId !== selectedMain.themeId) {
                        updates.push({ themeId: originalMain.themeId!, isMain: false, images: [] });
                        updates.push({ themeId: selectedMain.themeId!, isMain: true, images: [] });
                    }

                    if (updates.length > 0) {
                        await updateThemesForUser(updates, productId);
                    }
                }

                toast.success('템플릿이 저장되었습니다.');
                onClose();
            } catch (error) {
                toast.error('템플릿 저장에 실패했습니다.');
                console.error(error);
            }
        }
    };
    return (
        <div className="template-modal-overlay" onClick={onClose}>
            <div className="template-modal-container relative" onClick={(e) => e.stopPropagation()}>
                {/* 전체 로딩 오버레이 */}
                {loading && (
                    <div className="absolute inset-0 z-[1001] flex items-center justify-center rounded-2xl bg-white/80">
                        <div className="flex flex-col items-center gap-4">
                            <div className="template-spinner"></div>
                            <p className="text-sm font-semibold text-gray-700">데이터를 불러오는 중...</p>
                        </div>
                    </div>
                )}
                {/* 업로드 중 로딩 오버레이 */}
                {isUploading && (
                    <div className="absolute inset-0 z-[1000] flex items-center justify-center rounded-2xl bg-black/50">
                        <div className="flex flex-col items-center gap-4 rounded-xl bg-white px-6 py-6">
                            <div className="template-spinner"></div>
                            <p className="text-sm font-semibold text-gray-800">이미지 업로드 중...</p>
                            <p className="text-xs text-gray-500">잠시만 기다려 주세요</p>
                        </div>
                    </div>
                )}
                {/* 헤더 */}
                <div className="template-modal-header">
                    <div>
                        <h2>{title}</h2>
                        <p style={{ fontSize: '12px', color: '#666', marginTop: '1px' }}>
                            선택한 상품에서 사용 가능한 스타일 이미지를 변경하세요
                        </p>
                    </div>
                    <button className="template-modal-close" onClick={onClose}>
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                            <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                        </svg>
                    </button>
                </div>

                <div className="flex flex-col flex-1 overflow-hidden">
                    {/* 테마 목록 */}
                    <TemplateThemeList
                        role={role}
                        availableThemes={availableThemes}
                        onLoadTheme={handleLoadTheme}
                        onRequestNewThemeInput={handleRequestNewThemeInput}
                        showNewThemeInput={showNewThemeInput}
                        newThemeName={newThemeName}
                        onChangeNewThemeName={handleNewThemeNameChange}
                        onSubmitNewTheme={handleAddNewTheme}
                        onCancelNewTheme={handleCancelNewThemeInput}
                        themes={themes}
                        newThemes={newThemes}
                        selectedMainTheme={selectedMainTheme}
                        selectedNewTheme={selectedNewTheme}
                        onSelectMainTheme={handleMainThemeSelect}
                        onOpenTheme={handleThemeClick}
                        onOpenNewTheme={handleOpenNewTheme}
                        uploadedImages={uploadedImages}
                        isDefaultTheme={isDefaultTheme}
                    />

                    {/* 모달 내 사이드바 */}
                    <TemplateSidebar
                        show={showSidebar}
                        onClose={handleCloseSidebar}
                        role={role}
                        loading={loading}
                        selectedThemeName={selectedThemeName}
                        selectedNewTheme={selectedNewTheme}
                        isDefaultThemeByName={isDefaultThemeByName}
                        showNewCategoryInput={showNewCategoryInput}
                        onShowNewCategoryInput={handleShowNewCategoryInput}
                        onCancelNewCategoryInput={handleCancelNewCategoryInput}
                        newCategoryName={newCategoryName}
                        onChangeNewCategoryName={handleNewCategoryNameChange}
                        onSubmitNewCategory={handleSubmitNewCategory}
                        categories={categories}
                        createList={createList}
                        uploadedImagesInfo={uploadedImagesInfo}
                        uploadedImages={uploadedImages}
                        onRestoreDefault={handleRestoreDefault}
                        onUploadFiles={handleUploadFiles}
                    />
                </div>

                {/* 푸터 */}
                <div className="template-modal-footer p-3 border-t border-gray-200">
                    <div className="flex items-center justify-end gap-2 w-full">
                        <button className="template-btn-cancel" onClick={onClose}>
                            취소
                        </button>
                        <div className="relative ml-2">
                            {hasAnyChanges && (
                                <div className="pointer-events-none absolute bottom-[125%] left-1/2 z-50 w-max -translate-x-1/2 rounded-lg bg-[#d32f2f] px-4 py-[10px] text-[15px] font-bold text-white shadow-[0_4px_12px_rgba(0,0,0,0.25)]">
                                    저장해야 변경사항이 반영됩니다!
                                    <div className="absolute -bottom-2 left-1/2 h-0 w-0 -translate-x-1/2 border-x-[8px] border-x-transparent border-t-[8px] border-t-[#d32f2f]" />
                                </div>
                            )}
                            <button
                                className="template-btn-save rounded-lg bg-[#28a745] px-6 py-[10px] text-sm font-semibold text-white transition-colors hover:bg-[#22953d] disabled:cursor-not-allowed disabled:bg-gray-300 disabled:text-gray-500"
                                onClick={handleSaveClick}
                                disabled={isDisabled}
                            >
                                저장하기
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default TemplateImageModal;
